import { ChangeEvent, FocusEvent, useCallback, useEffect, useMemo, useState } from 'react';
import ReactCurrencyInput, {
  CurrencyInputOnChangeValues,
  CurrencyInputProps as ReactCurrencyInputProps,
} from 'react-currency-input-field';
import { EventOrValueHandler } from 'redux-form';
import { fieldStyles } from 'shared/styles/form';
import styled from 'styled-components';

import FormControlProps from 'lib/interfaces/FormControlProps';

export interface CurrencyInputProps
  extends FormControlProps,
    Omit<ReactCurrencyInputProps, 'value' | 'onBlur' | 'onChange' | 'onValueChange'> {
  // Whether or not the autoFocus prop should be passed to the underlying input component.
  // Using `enableAutofocus` rather than `autoFocus` is intentional. If `autoFocus` is not
  // explicitly passed to an `input` component, it is stripped and becomes a no-op.
  //
  // WARN: `autoFocus` prop gets special polyfill treatment by React due to browser DOM incompatibilities.
  // For React to do this, the prop needs to be explicitly passed to the `input` component.
  // See: https://github.com/facebook/react/blob/67e3f3fb6e342f95f00215c84d5d013d7b0e1b33/packages/react-dom/src/client/ReactDOMComponent.js#L348-L351
  //
  // TODO: Move to FocusScope API if and only if the RFC passes:
  // https://github.com/reactjs/rfcs/issues/104.
  onBlur: EventOrValueHandler<FocusEvent<HTMLInputElement>>;
  onChange: EventOrValueHandler<ChangeEvent<HTMLInputElement>>;
  value: number | string | undefined;
  enableAutofocus?: boolean;
}

// These languages are broken since the component doesn't support
// the numerical script used.
const BROKEN_LANGUAGES = ['ar', 'bn'];

const getDecimalsInfo = (
  intlConfig: ReactCurrencyInputProps['intlConfig'],
): { decimalLength?: number; decimalSymbol?: string; currencySymbol?: string } => {
  if (!intlConfig) {
    return {};
  }

  const parts = Intl.NumberFormat(intlConfig.locale, {
    style: 'currency',
    currency: intlConfig.currency,
    // Number here is chosen arbitrarily to get the maximum decimal length for the configured currency.
    // The number will be truncated to the maximum decimal length supported by the currency.
  }).formatToParts(0.1111111111111);

  return {
    decimalLength: parts.find((part) => part.type === 'fraction')?.value?.length ?? 0,
    decimalSymbol: parts.find((part) => part.type === 'decimal')?.value,
    currencySymbol: parts.find((part) => part.type === 'currency')?.value,
  };
};

const ReactCurrencyInputWrapper = ({
  id,
  hasError,
  hasWarning,
  enableAutofocus,
  onBlur,
  onChange,
  value,
  intlConfig,
  decimalScale: decimalScaleProp,
  maxLength: maxLengthProp,
  prefix: prefixProp,
  suffix: suffixProp,
  disableGroupSeparators: disableGroupSeparatorsProp,
  decimalSeparator: decimalSeparatorProp,
  ...otherProps
}: CurrencyInputProps) => {
  // internal value tracks the value emitted by react currency input component
  // this component expects this internal value to be passed back as its value
  // or else its state will reset, which can cause cursor weirdness as well as
  // certain inputs to be ignored.
  // e.g. when '34.' is inputted, internal value is '34.' but the redux-form value
  // is 34. passing redux-form value back to the component will cause the
  // decimal input to be ignored.
  // as another example, when '-' is entered the internal value is undefined,
  // we don't want to pass in empty string from redux form or else negative sign
  // input will be ignored.
  // initialize value as undefined to match the internal value of an empty input,
  // prefill will be handled with the later useEffect block which syncs value
  const [internalValue, setInternalValue] = useState<string | undefined>(undefined);

  const { decimalLength, decimalSymbol, currencySymbol } = useMemo(
    () => getDecimalsInfo(intlConfig),
    [intlConfig],
  );

  const decimalScale = useMemo(() => {
    // decimalScale prop pads the input with decimal places upon blur
    // only automatically pad if a decimal place is present to allow for non-decimal dollar inputs,
    // e.g. $1.8 => $1.80
    // $1.0 => $1.00
    // $1 => $1
    if (decimalScaleProp) {
      return decimalScaleProp;
    }

    if (decimalLength && decimalLength > 0 && decimalSymbol) {
      if (typeof internalValue === 'string') {
        return internalValue.includes(decimalSymbol) ? decimalLength : undefined;
      } else if (typeof internalValue === 'number') {
        return internalValue % 1 > 0 ? decimalLength : undefined;
      }
    }
  }, [decimalLength, decimalSymbol, decimalScaleProp, internalValue]);

  const handleChange = useCallback(
    (
      value: string | undefined,
      _name?: string | undefined,
      values?: CurrencyInputOnChangeValues | undefined,
    ) => {
      // this function is called on blur with incorrect values.float when dealing with
      // non-decimal currencies. ignore this call if the current value is equal to
      // internalValue
      if (value !== internalValue) {
        if (values?.float != null) {
          // ignore values 9,999,999,999,999,999 and higher since those round up due to floating point precision
          if (Math.abs(values.float) <= 1e16 - 2) {
            setInternalValue(value);
            onChange(values.float);
          }
        } else {
          setInternalValue(value);
          // convert undefined / null to empty string when passing back to redux form
          onChange('');
        }
      }
    },
    [onChange, internalValue],
  );

  // sync internal value with value passed form redux-form if it is a different number,
  // after considering null / undefined / empty string as the same
  useEffect(() => {
    if (Number(value ?? '') !== Number(internalValue ?? '')) {
      setInternalValue((value ?? '').toString());
    }
  }, [value]);

  const intlConfigIsBroken =
    !!intlConfig && BROKEN_LANGUAGES.includes(intlConfig?.locale?.split?.('-')?.[0]);

  return (
    <ReactCurrencyInput
      autoFocus={enableAutofocus}
      value={internalValue}
      onValueChange={handleChange}
      decimalScale={decimalScale}
      // entering more than 16 digits causes parseFloat to return imprecise values
      // set the max length to 17 since react currency input counts the decimal separator
      // as a character, the check in handleChange should disallow values without decimals
      // that contain more than 16 characters even though the max length is set to 17 here
      // Note that this will stop further inputs when we have something like $111111111111111.1,
      // But those should resolve to $111111111111111.10 on blur, and that value does parse
      // properly as a float.
      // This will also restrict further inputs when we have something like $1111111111111111.
      // Don't think handling these edge cases more nicely is worth the additional complexity in this component though
      maxLength={Math.min(maxLengthProp ?? 17, 17)}
      {...otherProps}
      // Needed to avoid blur sending unformatted value. Without this, the
      // value will become invalid on blur when used in Redux-Form <Field/>.
      // This fix is copied from phone number input but the same issue still applies
      // https://github.com/catamphetamine/react-phone-number-input/issues/49#issuecomment-316990240
      onBlur={() => onBlur?.(undefined)}
      aria-invalid={hasError}
      aria-errormessage={`${id}--error`}
      aria-describedby={`${id}--error`}
      id={id}
      // When we detect these broken languages, don't pass in intlConfig so western numerals are used
      // Instead use the computed decimal separator + currency suffix (both ar and bn use suffix) + disable group separator
      // However, if the prop has been passed from above, always use the prop
      intlConfig={intlConfigIsBroken ? undefined : intlConfig}
      prefix={prefixProp ?? (intlConfigIsBroken ? '' : undefined)}
      suffix={suffixProp ?? (intlConfigIsBroken ? currencySymbol : undefined)}
      decimalSeparator={decimalSeparatorProp ?? (intlConfigIsBroken ? decimalSymbol : undefined)}
      disableGroupSeparators={disableGroupSeparatorsProp ?? intlConfigIsBroken}
      // Always disable abbreviations because sometimes currency suffixes can be interpreted as an abbreviation
      // E.g. 1 MX$ -> 1M = 1 million
      disableAbbreviations={true}
    />
  );
};

const CurrencyInput = styled(ReactCurrencyInputWrapper)<CurrencyInputProps>`
  ${(props) => fieldStyles(props, props.theme)};
`;

export default CurrencyInput;
